import React from 'react';
import { Button, Tooltip } from 'antd';
import type { ButtonProps } from 'antd';
import { usePermission } from '../../hooks/usePermission';

interface PermissionButtonProps extends ButtonProps {
  /** 必要な権限レベル */
  requiredPermission: 'edit' | 'delete' | 'manage';
  children: React.ReactNode;
}

/**
 * 権限チェック付きボタンコンポーネント
 * 権限がない場合はボタンを無効化し、ツールチップで理由を表示する
 */
export const PermissionButton = React.forwardRef<HTMLSpanElement, PermissionButtonProps>(function PermissionButton(
  { requiredPermission, children, disabled, onClick, ...props }: PermissionButtonProps,
  ref
) {
  const { canEdit, canDelete, canManage, getDisabledMessage } = usePermission();

  const hasPermission =
    requiredPermission === 'edit'
      ? canEdit
      : requiredPermission === 'delete'
        ? canDelete
        : requiredPermission === 'manage'
          ? canManage
          : true;

  const disabledMessage = getDisabledMessage(requiredPermission);
  const isDisabled = disabled || !hasPermission;

  // 権限がない場合はクリックイベントを無効化（モーダルを開かせない）
  const handleClick = hasPermission ? onClick : undefined;

  const inner = !hasPermission && disabledMessage ? (
    <Tooltip title={disabledMessage}>
      <span style={{ cursor: 'not-allowed', display: 'inline-block' }}>
        <Button {...props} disabled style={{ pointerEvents: 'none' }}>
          {children}
        </Button>
      </span>
    </Tooltip>
  ) : (
    <Button {...props} disabled={isDisabled} onClick={handleClick}>
      {children}
    </Button>
  );

  return (
    <span ref={ref} style={{ display: 'inline-block' }}>
      {inner}
    </span>
  );
});

PermissionButton.displayName = 'PermissionButton';
